import React, {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "swiper/swiper.min.css";
import { config, useSpring } from "@react-spring/core";
import { animated } from "@react-spring/web";
import { isMobile } from "react-device-detect";
import useIntersectionObserver from "@react-hook/intersection-observer";
import styled from "styled-components";
import Image from "next/image";
import Link from "next/link";
import CommonService from "domain/common/commonService";
import { useTheme } from "lib/contexts/themeContext";
import PageLayout from "lib/components/PageLayout";
import ModalDownloadApp from "lib/components/ModalDownloadApp";
import QRAppDownloadCaseTracker from "lib/assets/qr-app-download-case-tracker.svg";
import Button from "lib/components/Button";
import Stack from "lib/components/Stack";
import useScript from "lib/hooks/useScript";
import IconRibbon from "lib/assets/ribbon.svg";
import IconStar from "lib/assets/landing-star.svg";
import IconDiamond from "lib/assets/diamond.svg";
import rive1 from "lib/assets/rive/desc1.riv";
import rive2 from "lib/assets/rive/desc2.riv";
import rive3 from "lib/assets/rive/desc3.riv";
import rive4 from "lib/assets/rive/desc4.riv";
import rive5 from "lib/assets/rive/desc5.riv";
import rive6 from "lib/assets/rive/desc6.riv";
import ImgMockDevice from "lib/assets/case-tracker-mock-device.png";
import ImgYH from "lib/assets/YH.png";
import ImgFT from "lib/assets/FT.svg";
import ImgAP from "lib/assets/AP.svg";
import ImgMW from "lib/assets/MW.svg";
import ImgScreenshot1 from "lib/assets/case-tracker-screenshot-1.png";
import ImgScreenshot2 from "lib/assets/case-tracker-screenshot-2.png";
import ImgScreenshot3 from "lib/assets/case-tracker-screenshot-3.png";
import IconVisaFamilyBased from "lib/assets/visa-family-based.svg";
import IconVisaEmploymentBased from "lib/assets/visa-employment-based.svg";
import IconVisaNonImmigrant from "lib/assets/visa-non-immigrant.svg";
import IconVisaOthers from "lib/assets/visa-others.svg";
import { UIEvent } from "lib/enums";
import { useEvent } from "lib/contexts/eventContext";
import SnackbarDownloadApp from "lib/components/SnackbarDownloadApp";
import Head from "lib/components/Head";
import Statistic from "lib/components/Statistic";
import ActionCloud from "lib/components/ActionCloud";
import ResourceLink from "lib/components/ResourceLink";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import InfiniteSlide from "lib/components/InfiniteSlide";
import AttorneyTestimonials from "lib/components/AttorneyTestimonial";
import { useTranslations } from "lib/i18n";
import ButtonDownloadApp from "lib/components/ButtonDownloadApp";

const TESTIMONIALS = [
  {
    name: "Gti*****",
    contentKey: "reviews.gti",
    color: "#f2f9ff",
  },
  {
    name: "Nas ******",
    contentKey: "reviews.nas",
    color: "#fbf3ff",
  },
  {
    name: "myh*****************",
    contentKey: "reviews.myh",
    color: "#eff4ff",
  },
  {
    name: "Edu*****",
    contentKey: "reviews.edu",
    color: "#faf7ff",
  },
];

const ViewCaseTracker = () => {
  const t = useTranslations("caseTracker");
  return (
    <PageLayout
      customCompMobile={
        <ButtonDownloadApp
          event={UIEvent.CaseTrackerMobileTopAppDownloadClicked}
        />
      }
    >
      <Head
        url="/case-tracker"
        title={t("meta.title")}
        desc={t("meta.description")}
      />
      <Hero />
      <Featured />
      <CheckYourCase />
      <Stats />
      <TestimonialsAttorney />
      <Description />
      <TypesVisaForm />
      <Testimonials />
      <CheckYourCase2 />
      <Resources />
    </PageLayout>
  );
};

export default ViewCaseTracker;

const Hero = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints, palette } = useTheme();

  return (
    <div
      css={`
        margin: 120px auto 160px;
        padding: 0 20px;
        ${breakpoints.down("sm")} {
          margin: 80px auto 100px;
        }
      `}
      id="section1"
    >
      <h1
        css={`
          margin: 0 auto 20px;
          max-width: 1000px;
          display: flex;
          flex-direction: column;
          line-height: 80px;
          font-size: 60px;
          font-weight: bold;
          ${breakpoints.down("sm")} {
            margin: 0 auto 30px;
            line-height: 56px;
            font-size: 40px;
          }
        `}
      >
        <span style={{ color: palette.primary.main }}>{t("hero.product")}</span>
        <p
          css={`
            margin: 0;
            display: flex;
            flex-direction: column;
            ${breakpoints.down("sm")} {
              line-height: 42px;
              font-size: 30px;
            }
          `}
        >
          <span>{t("hero.tagline")}</span>
          <span>{t("hero.taglineObject")}</span>
        </p>
      </h1>
      <div
        css={`
          position: relative;
          margin: 0 auto;
          padding-left: 60px;
          max-width: 1120px;
          display: flex;
          justify-content: space-between;
          ${breakpoints.down(1220)} {
            max-width: 1000px;
            padding-left: 0;
          }
          ${breakpoints.down("sm")} {
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            margin-top: 10px;
            line-height: 34px;
            font-size: 24px;
            font-weight: 700;
            ${breakpoints.down("sm")} {
              display: none;
            }
          `}
        >
          {t("hero.headline")}
          <br />
          {t("hero.subheadline")}
        </div>
        <div
          css={`
            position: relative;
            margin-top: -20px;
            width: 60vw;
            max-width: 600px;
            ${breakpoints.down("sm")} {
              margin: 0 auto;
              width: 100%;
              max-width: 500px;
            }
          `}
        >
          <PhoneSlides />
          <div
            css={`
              position: absolute;
              right: 5.5%;
              top: -15%;
              ${breakpoints.down("sm")} {
                display: none;
              }
            `}
          >
            <IconDiamond />
          </div>
          <div
            css={`
              position: absolute;
              left: -33.5%;
              bottom: -10%;
              ${breakpoints.down("sm")} {
                display: none;
              }
            `}
          >
            <IconStar />
          </div>
        </div>
      </div>
    </div>
  );
};

const PhoneSlides = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setIndex((i) => (i + 1) % 3);
    }, 3500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div
      css={`
        position: relative;
      `}
    >
      <div
        css={`
          position: absolute;
          top: 4.5%;
          left: 33%;
          width: 45%;
          overflow: hidden;
        `}
      >
        <div
          css={`
            width: 300%;
            height: auto;
            display: flex;
            transform: translateX(${-index * (100 / 3)}%);
            transition: transform 0.55s ease-in-out;
          `}
        >
          <div
            css={`
              width: 100%;
              max-width: ${100 / 3}%;
            `}
          >
            <Image
              unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
              priority
              src={ImgScreenshot1}
              width={2.5 * 272}
              height={2.5 * 440}
            />
          </div>
          <div
            css={`
              width: 100%;
              max-width: ${100 / 3}%;
            `}
          >
            <Image
              unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
              src={ImgScreenshot2}
              width={2.5 * 272}
              height={2.5 * 440}
            />
          </div>
          <div
            css={`
              width: 100%;
              max-width: ${100 / 3}%;
            `}
          >
            <Image
              css={`
                &&& {
                  width: 100%;
                  max-width: ${100 / 3}%;
                }
              `}
              unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
              src={ImgScreenshot3}
              width={2.5 * 272}
              height={2.5 * 440}
            />
          </div>
        </div>
      </div>
      <Image
        css={`
          position: relative;
          z-inex: 0;
        `}
        unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
        priority
        src={ImgMockDevice}
        width={1200}
        height={920}
      />
    </div>
  );
};

const Featured = () => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin-bottom: 160px;
        padding: 0 20px;
        height: 164px;
        display: flex;
        align-items: center;
        background-color: #f7f9fd;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section2"
    >
      <div
        css={`
          margin: 0 auto;
          max-width: 900px;
          width: 100%;
          display: flex;
          justify-content: space-between;
          ${breakpoints.down("sm")} {
            display: none;
          }
        `}
      >
        <Image
          unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
          src={ImgYH}
          width={107}
          height={64}
        />
        <ImgFT />
        <ImgAP />
        <ImgMW />
      </div>
      <div
        css={`
          margin: 0 auto;
          max-width: 900px;
          width: 100%;
          display: none;
          justify-content: space-between;
          ${breakpoints.down("sm")} {
            display: flex;
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            margin-bottom: 10px;
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          <div
            css={`
              margin-right: 20px;
            `}
          >
            <Image
              unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
              src={ImgYH}
              width={67}
              height={40}
            />
          </div>
          <ImgFT width={151} height={40} />
        </div>
        <div
          css={`
            display: flex;
            justify-content: center;
          `}
        >
          <ImgAP
            css={`
              margin-right: 20px;
            `}
            width={30}
            height={40}
          />
          <ImgMW width={114} height={40} />
        </div>
      </div>
    </div>
  );
};

const CheckYourCase = () => {
  const t = useTranslations("caseTracker");
  const { track } = useEvent();
  const { breakpoints } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOnClick = useCallback(() => {
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "Case Tracker", "Cloud CTA Top"),
        "_blank",
      );
      track(UIEvent.CaseTrackerMobileAppDownload);
    } else {
      setIsModalOpen(true);
      sessionStorage.setItem("disableDownloadApp", "true");
      track(UIEvent.CaseTrackerDesktopAppDownload);
    }
  }, [track]);

  const handleOnClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div
      css={`
        overflow: hidden;
        position: relative;
        margin: 0 auto 160px;
        max-width: 940px;
        height: 304px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        ${breakpoints.down("sm")} {
          margin: 0 auto 100px;
          max-width: 1152px;
          height: 398px;
        }
      `}
      id="section3"
    >
      <div
        css={`
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 940px;
          height: 304px;
          ${breakpoints.down("sm")} {
            transform: translate(-50%, -50%) scale(1.3);
          }
        `}
      >
        <IconRibbon />
      </div>

      <div
        css={`
          position: relative;
        `}
      >
        <div
          css={`
            margin: 0 20px 20px;
            max-width: 590px;
            line-height: 28px;
            font-size: 20px;
            font-weight: 700;
          `}
        >
          {t("cta.question")}
          <br />
          <span>{t("cta.description")}</span>
        </div>
        <div
          css={`
            margin: 0 auto;
            padding: 0 20px;
            width: 280px;
            display: flex;
            justify-content: center;
            align-items: center;
            ${breakpoints.down("sm")} {
              width: 100%;
              max-width: 322px;
            }
          `}
        >
          <ButtonLight onClick={handleOnClick}>{t("cta.button")}</ButtonLight>
        </div>
      </div>

      <ModalDownloadApp
        qr={QRAppDownloadCaseTracker}
        open={isModalOpen}
        onClose={handleOnClose}
      />
    </div>
  );
};

const ButtonLight = styled(Button)`
  &&.MuiButton-root {
    height: 60px;
    line-height: 26px;
    font-size: 18px;
    font-weight: 500;
    background-color: #00b4ff;
    :hover {
      background-color: #00a4ed;
    }
  }
`;

const Stats = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints } = useTheme();
  const [numCases, setNumCases] = useState(306590);
  const [numNationalities, setNumNationalities] = useState(191);
  const container = useRef<HTMLDivElement>(null);
  const done = useRef(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.3,
  });
  const [styles, api] = useSpring(() => ({
    nCases: 0,
    nNationalities: 0,
    config: config.molasses,
  }));

  useEffect(() => {
    new CommonService()
      .getLawfullyStats()
      .then(({ numCases, numCountries }) => {
        setNumCases(numCases);
        setNumNationalities(numCountries);
      });
    return () => {
      done.current = false;
    };
  }, []);

  useEffect(() => {
    if (!done.current && isIntersecting) {
      done.current = true;
      api.start({
        nCases: numCases,
        nNationalities: numNationalities,
      });
    }
  }, [api, isIntersecting, numCases, numNationalities]);

  return (
    <div
      css={`
        margin: 0 auto 160px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section4"
      ref={container}
    >
      <div
        css={`
          margin: 0 auto 60px;
          padding: 0 20px;
          max-width: 760px;
          line-height: 56px;
          font-size: 40px;
          font-weight: 700;
          text-align: center;
          ${breakpoints.down("sm")} {
            margin-bottom: 40px;
            line-height: 42px;
            font-size: 30px;
          }
        `}
      >
        {t("stats.intro")}
      </div>
      <div
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          ${breakpoints.down("sm")} {
            grid-template-columns: 1fr;
          }
        `}
      >
        <Statistic
          css={`
            padding: 20px 0;
          `}
          title={t("stats.registeredCases")}
          suffix="+"
          value={
            // eslint-disable-next-line
            // @ts-ignore
            <animated.span>
              {styles.nCases.to((n) =>
                new Intl.NumberFormat().format(Math.round(n)),
              )}
            </animated.span>
          }
        />
        <Statistic
          css={`
            padding: 20px 0;
          `}
          title={t("stats.userNationalities")}
          suffix="+"
          value={
            <animated.span>
              {styles.nNationalities.to((n) =>
                new Intl.NumberFormat().format(Math.round(n)),
              )}
            </animated.span>
          }
        />
      </div>
    </div>
  );
};

interface DescriptionItemProps {
  bg: string;
  index: number;
  rive: any;
  animationReady: boolean;
  reverse?: boolean;
}

const DescriptionItem: React.FC<DescriptionItemProps> = ({
  bg,
  index,
  rive,
  animationReady,
  reverse,
  children,
}) => {
  const { breakpoints } = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const [isAnimPlaying, setIsAnimPlaying] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.3,
  });

  useEffect(() => {
    if (animationReady) {
      if (isIntersecting) {
        setIsAnimPlaying(true);
      } else {
        setIsAnimPlaying(false);
      }
    }
  }, [animationReady, index, isIntersecting, rive]);

  return (
    <DescriptionItemContainer
      css={`
        background-color: ${bg};
      `}
      ref={container}
    >
      <div
        css={`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          width: 100%;
          max-width: 1000px;
          ${breakpoints.down("sm")} {
            margin: 60px 0;
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            width: 100%;
            max-width: 490px;
            order: ${reverse ? 2 : 1};
            ${breakpoints.down("sm")} {
              margin-bottom: 20px;
              order: 1;
            }
          `}
        >
          {children}
        </div>
        <div
          css={`
            width: 100%;
            max-width: 400px;
            order: ${reverse ? 1 : 2};
            ${breakpoints.down("sm")} {
              order: 2;
              display: flex;
              justify-content: center;
            }
          `}
        >
          <div
            css={`
              width: 100%;
              height: 0;
              padding-bottom: 75%;
            `}
          >
            <div
              css={`
                max-width: 400px;
              `}
            >
              {isAnimPlaying && (
                <RiveCanvas id={`riveCanvas${index}`} src={rive} />
              )}
            </div>
          </div>
        </div>
      </div>
    </DescriptionItemContainer>
  );
};

interface RiveCanvasProps {
  id: string;
  src: any;
}

const RiveCanvas: React.FC<RiveCanvasProps> = ({ id, src }) => {
  useEffect(() => {
    new window.rive.Rive({
      src,
      canvas: document.getElementById(id),
      layout: new window.rive.Layout({ fit: "contain" }),
      autoplay: true,
    });
  }, [id, src]);

  return <canvas style={{ width: "100%" }} id={id} width={800} height={600} />;
};

const DescriptionItemContainer = styled.div`
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 42px;
  font-size: 30px;
  font-weight: bold;
  color: white;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: auto;
    line-height: 34px;
    font-size: 24px;
  }
`;

const Description = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints } = useTheme();
  const status = useScript("https://unpkg.com/rive-js@0.7.11/dist/rive.min.js");
  const container = useRef<HTMLDivElement>(null);
  const isRiveReady = useMemo(() => status === "ready", [status]);
  const [isSnackbarRendered, setIsSnackbarRendered] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.1,
  });

  useEffect(() => {
    if (!isSnackbarRendered && isIntersecting) {
      setIsSnackbarRendered(true);
    }
  }, [isSnackbarRendered, isIntersecting]);

  return (
    <div
      css={`
        margin-bottom: 158px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section5"
      ref={container}
    >
      <DescriptionItem
        index={0}
        bg="#6400ff"
        animationReady={isRiveReady}
        rive={rive1}
      >
        <DescriptionItemTitle>
          {t("features.notifications.title")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.notifications.description")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={1}
        bg="#BF1ECE"
        animationReady={isRiveReady}
        reverse
        rive={rive2}
      >
        <DescriptionItemTitle>
          {t("features.analysis.title")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.analysis.description")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={2}
        bg="#00B4FF"
        animationReady={isRiveReady}
        rive={rive3}
      >
        <DescriptionItemTitle>
          {t("features.timeline.title")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.timeline.description")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={3}
        bg="#325AFF"
        reverse
        animationReady={isRiveReady}
        rive={rive4}
      >
        <DescriptionItemTitle>
          {t("features.advanced.title")}
          <br />
          <span
            css={`
              padding-right: 12px;
              vertical-align: middle;
            `}
          >
            {t("features.advanced.titleSuffix")}
          </span>
          <span
            css={`
              border-radius: 8px;
              padding: 4px 8px;
              line-height: 24px;
              font-size: 16px;
              background-color: #00b4ff;
              vertical-align: middle;
            `}
          >
            {t("features.advanced.badge")}
          </span>
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          <div style={{ marginBottom: 10 }}>
            {t("features.advanced.descriptionBefore")}{" "}
            <Link
              href="https://www.lawfully.com/resources/what-distinguishes-case-analysis-pro-from-other-case-trackers#what-is-your-ranking"
              passHref
            >
              <a
                css={`
                  padding: 0 2px;
                  color: white;
                  text-decoration: underline;
                  :hover {
                    background-color: #00b4ff;
                    color: white;
                  }
                `}
                target="_blank"
              >
                {t("features.advanced.descriptionLink")}
              </a>
            </Link>{" "}
            {t("features.advanced.descriptionAfter")}
          </div>
          <div>{t("features.advanced.exclusive")}</div>
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={4}
        bg="#FF783C"
        animationReady={isRiveReady}
        rive={rive5}
      >
        <DescriptionItemTitle>
          {t("features.visaBulletin.title")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.visaBulletin.description")}
          <br />
          <br />
          {t("features.visaBulletin.descriptionExtra")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={5}
        bg="#AA00FF"
        animationReady={isRiveReady}
        rive={rive6}
      >
        <DescriptionItemTitle>{t("features.nvc.title")}</DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.nvc.description")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      {/* Snackbar */}
      {isSnackbarRendered && <SnackbarDownloadApp />}
    </div>
  );
};

const DescriptionItemTitle = styled.div`
  margin-bottom: 20px;
  line-height: 42px;
  font-size: 30px;
  font-weight: 700;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding-right: 20px;
    line-height: 34px;
    font-size: 24px;
  }
`;

const DescriptionItemSubtitle = styled.div`
  padding-right: 20px;
  line-height: 28px;
  font-size: 20px;
  font-weight: 500;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    line-height: 24px;
    font-size: 16px;
    font-weight: 500;
  }
`;

const TypesVisaForm = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section6"
    >
      <SectionTitle>{t("types.title")}</SectionTitle>
      <div
        css={`
          margin-bottom: 59px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          column-gap: 22px;
          text-align: center;
          ${breakpoints.down("sm")} {
            margin-bottom: 87px;
            grid-template-columns: 1fr;
          }
        `}
      >
        <TypesItem
          image={<IconVisaFamilyBased />}
          title={t("types.familyBased")}
          description="IR-1, CR-1, IR-2, IR-5, F1, F2A, F2B, F3, F4, K1/K2"
        />
        <TypesItem
          image={<IconVisaEmploymentBased />}
          title={t("types.employmentBased")}
          description="EB1-1, EB1-2, EB1-3, EB2-1, EB2-NIW, EB3-1, EB3-2, EB3-3, EB4, EB5"
        />
        <TypesItem
          image={<IconVisaNonImmigrant />}
          title={t("types.nonImmigrant")}
          description="H-1B, H-1C, H-2A, H-2B, H-3, TN, DV, Blanket L(L-1, L-2), O, P, R, E-3, K-1, K-2, K-3, K-4, F, J, M"
        />
        <TypesItem
          image={<IconVisaOthers />}
          title={t("types.others")}
          description="T, U, VAWA, Q, E, Naturalization"
        />
      </div>

      <div
        css={`
          border-radius: 10px;
          padding: 30px;
          display: flex;
          align-items: center;
          background-color: #f9f9fb;
          ${breakpoints.down("sm")} {
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            margin-right: 95px;
            line-height: 34px;
            font-size: 24px;
            font-weight: 500;
            ${breakpoints.down("sm")} {
              margin: 0 0 20px 0;
              text-align: center;
            }
          `}
        >
          {t("types.formsTitle")}
        </div>
        <div
          css={`
            flex: 1;
            line-height: 24px;
            font-size: 16px;
          `}
        >
          I-102, I-129, I-129F, I-130, I-131, I-140, I-360, I-485, I-526, I-539,
          I-601, I-601A, I-751, I-765, I-821D, I-824, I-829, I-90, N-400, N-600
        </div>
      </div>
    </div>
  );
};

interface TypesItemProps {
  image: ReactNode;
  title: ReactNode;
  description: string;
}

const TypesItem: React.FC<TypesItemProps> = ({ image, title, description }) => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin-top: 30px;
        ${breakpoints.down("sm")} {
          margin-bottom: 60px;
          :last-of-type {
            margin-bottom: 0;
          }
        }
      `}
    >
      <div
        css={`
          margin-bottom: 30px;
        `}
      >
        {image}
      </div>
      <div
        css={`
          margin-bottom: 20px;
          min-height: 68px;
          line-height: 34px;
          font-size: 24px;
          font-weight: 500;
          ${breakpoints.down("sm")} {
            min-height: 0;
          }
        `}
      >
        {title}
      </div>
      <div
        css={`
          line-height: 24px;
          font-size: 16px;
        `}
      >
        {description}
      </div>
    </div>
  );
};

const Testimonials: React.FC = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        overflow: hidden;
        margin-bottom: 160px;
        ${breakpoints.down("sm")} {
          margin-bottom: 160px;
        }
      `}
    >
      <div
        css={`
          margin-bottom: 40px;
        `}
      >
        <div
          css={`
            margin-bottom: 60px;
            line-height: 56px;
            font-size: 40px;
            font-weight: 700;
            text-align: center;
            ${breakpoints.down("sm")} {
              line-height: 42px;
              font-size: 30px;
            }
          `}
        >
          {t("testimonials.title")}
        </div>
      </div>

      {/* Large view */}
      <InfiniteSlide
        css={`
          ${breakpoints.down("sm")} {
            display: none;
          }
        `}
        itemWidth={490}
      >
        {TESTIMONIALS.map((data, i) => (
          <Testimonial key={i} name={data.name} color={data.color}>
            {t(data.contentKey)}
          </Testimonial>
        ))}
      </InfiniteSlide>

      {/* Small view */}
      <InfiniteSlide
        css={`
          display: none;
          margin-bottom: 20px;
          ${breakpoints.down("sm")} {
            display: block;
          }
        `}
        itemWidth={300}
      >
        {TESTIMONIALS.map((data, i) => (
          <Testimonial key={i} name={data.name} color={data.color}>
            {t(data.contentKey)}
          </Testimonial>
        ))}
      </InfiniteSlide>
    </div>
  );
};

interface TestimonialProps {
  name: string;
  color: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ name, color, children }) => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        border-radius: 30px;
        padding: 60px;
        display: flex;
        flex-direction: column;
        background-color: ${color};
        ${breakpoints.down("sm")} {
          padding: 30px;
        }
      `}
    >
      <div
        css={`
          margin-bottom: 20px;
          line-height: 28px;
          font-size: 20px;
          font-weight: bold;
        `}
      >
        {name}
      </div>
      <div
        css={`
          line-height: 24px;
          font-size: 16px;
          white-space: pre-wrap;
        `}
      >
        {children}
      </div>
    </div>
  );
};

const TestimonialsAttorney = () => {
  const t = useTranslations("caseTracker");
  return (
    <div
      css={`
        margin-bottom: 160px;
      `}
    >
      <SectionTitle
        css={`
          padding: 0 20px;
        `}
      >
        {t("attorneys.title")}
      </SectionTitle>
      <AttorneyTestimonials />
    </div>
  );
};

const CheckYourCase2 = () => {
  const t = useTranslations("caseTracker");
  const { track } = useEvent();
  const { breakpoints } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOnClick = useCallback(() => {
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "Case Tracker", "Cloud CTA Bottom"),
        "_blank",
      );
      track(UIEvent.CaseTrackerMobileAppDownload);
    } else {
      setIsModalOpen(true);
      sessionStorage.setItem("disableDownloadApp", "true");
      track(UIEvent.CaseTrackerDesktopAppDownload);
    }
  }, [track]);

  const handleOnClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div
      css={`
        margin: 0 auto 160px;
        ${breakpoints.down("sm")} {
          margin: 0 auto 96px;
        }
      `}
      id="section8"
    >
      <ActionCloud
        text={t("download.prompt")}
        buttonText={t("download.button")}
        onClick={handleOnClick}
      />
      <ModalDownloadApp
        qr={QRAppDownloadCaseTracker}
        open={isModalOpen}
        onClose={handleOnClose}
      />
    </div>
  );
};

const Resources = () => {
  const t = useTranslations("caseTracker");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin: 0 auto 100px;
        }
      `}
      id="section9"
    >
      <SectionTitle
        css={`
          ${breakpoints.down("sm")} {
            line-height: 34px;
            font-size: 24px;
          }
        `}
      >
        {t("resources.title")}
      </SectionTitle>
      <Stack spacing="10px">
        <ResourceLink
          title="You May Be Eligible for Reduced Fees and Fee Waiver"
          href="/resources/reduced-fees-and-fee-waiver"
        />
        <ResourceLink
          title="How Long Does It Take to Get a Marriage-Based Green Card?"
          href="/resources/how-long-does-it-take-to-get-a-marriage-based-green-card"
        />
        <ResourceLink
          title="How is the minimum income requirement for Green Card sponsorship"
          href="/resources/what-are-the-income-requirements-for-a-marriage-green-card-sponsor#i-how-is-the-minimum-income-requirement-for-green-card-sponsorship-calculated"
        />
        <ResourceLink
          title="Common Reasons Citizenship Application are Denied"
          href="/resources/common-n400-form-mistakes#why-are-some-us-citizenship-applications-denied"
        />
        <ResourceLink
          title="How Long Does It Take to Become a U.S. Citizen"
          href="/resources/naturalization-processing-time#i-naturalization-processing-time-overview-and-the-general-citizenship-timeline"
        />
      </Stack>
    </div>
  );
};

const SectionTitle = styled.h2`
  margin-bottom: 60px;
  text-align: center;
  line-height: 56px;
  font-size: 40px;
  ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
    margin-bottom: 40px;
    line-height: 42px;
    font-size: 30px;
  }
`;
